import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import { startLoading, stopLoading, setTaskMessage, LOADING_TYPES } from "../loading";
import { canAccessSalesDataImport } from "../features/salesDataImport/access";
import SalesImportNav from "../features/salesDataImport/components/SalesImportNav";
import {
  SALES_IMPORT_BRANCH_OPTIONS,
  salesImportBranchDisplayName,
} from "../features/salesDataImport/config/salesImportBranchIds";
import { RECORDS_PAGE_SIZE } from "../features/salesDataImport/constants";
import {
  BRANCH_REPORT_DEFAULT_BRANCH_ID,
  BRANCH_VOUCHER_ALL_BRANDS,
  BRANCH_VOUCHER_NO_BRAND,
  BRANCH_VOUCHER_REPORT_TYPE_KEYS,
  buildBranchVoucherMonthwiseRows,
  buildBranchVoucherReportsBundle,
  fetchBranchVoucherReportsBundle,
  resolveBranchVoucherViewReport,
} from "../features/salesDataImport/services/branchVoucherReportService";
import {
  exportBranchVoucherMonthwiseExcel,
  exportBranchVoucherVouchersExcel,
} from "../features/salesDataImport/services/branchVoucherReportExportService";
import { formatFirebaseError } from "../features/salesDataImport/utils/firebaseError";
import "./SalesDataImportCenter.css";
import "./BranchVoucherReportPage.css";

const DEFAULT_VIEW_KEY = "outward-credit-br";
const ALL_MONTHS = "all";

function fm(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function defaultFilters() {
  return {
    year: new Date().getFullYear(),
    branchId: BRANCH_REPORT_DEFAULT_BRANCH_ID,
  };
}

export default function BranchVoucherReportPage() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const [dark, setDark] = useState(() => localStorage.getItem("sdic-dark") === "1");
  const [draft, setDraft] = useState(defaultFilters);
  const [applied, setApplied] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataBundle, setDataBundle] = useState(null);
  const [brandName, setBrandName] = useState(BRANCH_VOUCHER_ALL_BRANDS);
  const [brandName2, setBrandName2] = useState(BRANCH_VOUCHER_NO_BRAND);
  const [viewKey, setViewKey] = useState(DEFAULT_VIEW_KEY);
  const [selectedMonthKey, setSelectedMonthKey] = useState(ALL_MONTHS);
  const [tablePage, setTablePage] = useState(0);
  const [search, setSearch] = useState("");

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, []);

  const bundle = useMemo(() => {
    if (!dataBundle?.rawRowsByType) return null;
    return buildBranchVoucherReportsBundle(dataBundle.rawRowsByType, {
      year: dataBundle.meta.year,
      branchId: dataBundle.meta.branchId,
      brandName,
      brandName2,
    });
  }, [dataBundle, brandName, brandName2]);

  const brandOptions = dataBundle?.meta?.brandOptions || [];

  useEffect(() => {
    if (brandName2 !== BRANCH_VOUCHER_NO_BRAND && brandName2 === brandName) {
      setBrandName2(BRANCH_VOUCHER_NO_BRAND);
    }
  }, [brandName, brandName2]);

  const activeReport = resolveBranchVoucherViewReport(bundle, viewKey);
  const monthwiseRows = useMemo(() => buildBranchVoucherMonthwiseRows(bundle), [bundle]);
  const activeRow = monthwiseRows.find((r) => r.viewKey === viewKey);
  const monthSlots = bundle?.meta?.months || [];

  const selectedMonthLabel = useMemo(() => {
    if (selectedMonthKey === ALL_MONTHS) return "All months";
    const slot = monthSlots.find((m) => m.monthKey === selectedMonthKey);
    return slot?.label || selectedMonthKey;
  }, [selectedMonthKey, monthSlots]);

  const loadBundle = useCallback(async (filters) => {
    if (!filters?.branchId) {
      toast.error("Select a branch.");
      return;
    }

    setLoading(true);
    const taskId = startLoading({
      type: LOADING_TYPES.REPORT,
      report: true,
      message: "Generating branch voucher reports…",
    });

    try {
      const out = await fetchBranchVoucherReportsBundle({
        year: filters.year,
        branchId: filters.branchId,
        onProgress: (rows, chunks) => {
          setTaskMessage(
            taskId,
            chunks
              ? `Loaded ${rows.toLocaleString("en-IN")} lines (${chunks} chunks)…`
              : `Loaded ${rows.toLocaleString("en-IN")} lines…`
          );
        },
      });
      setDataBundle(out);
      setBrandName(BRANCH_VOUCHER_ALL_BRANDS);
      setBrandName2(BRANCH_VOUCHER_NO_BRAND);
      setViewKey(DEFAULT_VIEW_KEY);
      setSelectedMonthKey(ALL_MONTHS);
      setTablePage(0);
      setSearch("");

      const totalVouchers = BRANCH_VOUCHER_REPORT_TYPE_KEYS.reduce(
        (s, key) => s + (out.reports[key]?.summary?.voucherCount || 0),
        0
      );
      const brandCount = out.meta.brandOptions?.length || 0;
      if (!totalVouchers) {
        toast.error("No vouchers found for this year and branch.");
      } else {
        toast.success(
          `${totalVouchers.toLocaleString()} voucher(s) for ${filters.year} · ${brandCount} brand(s)`
        );
      }
    } catch (e) {
      toast.error(formatFirebaseError(e));
      setDataBundle(null);
    } finally {
      stopLoading(taskId);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!applied) return;
    loadBundle(applied);
  }, [applied, loadBundle]);

  useEffect(() => {
    localStorage.setItem("sdic-dark", dark ? "1" : "0");
  }, [dark]);

  useEffect(() => {
    setTablePage(0);
    setSearch("");
  }, [viewKey, selectedMonthKey, brandName, brandName2]);

  const filteredVouchers = useMemo(() => {
    let vouchers = activeReport?.vouchers || [];
    if (selectedMonthKey !== ALL_MONTHS) {
      vouchers = vouchers.filter((v) => v.monthKey === selectedMonthKey);
    }
    const term = search.trim().toLowerCase();
    if (!term) return vouchers;
    return vouchers.filter(
      (v) =>
        String(v.voucherNo || "").toLowerCase().includes(term) ||
        String(v.customerName || "").toLowerCase().includes(term) ||
        String(v.employeeName || "").toLowerCase().includes(term) ||
        String(v.voucherType || "").toLowerCase().includes(term)
    );
  }, [activeReport, selectedMonthKey, search]);

  const pagedVouchers = useMemo(() => {
    const start = tablePage * RECORDS_PAGE_SIZE;
    return filteredVouchers.slice(start, start + RECORDS_PAGE_SIZE);
  }, [filteredVouchers, tablePage]);

  const handleSearch = () => {
    if (!draft.branchId) {
      toast.error("Branch is required.");
      return;
    }
    setApplied({ ...draft });
  };

  const handleReset = () => {
    setDraft(defaultFilters());
    setApplied(null);
    setDataBundle(null);
    setBrandName(BRANCH_VOUCHER_ALL_BRANDS);
    setBrandName2(BRANCH_VOUCHER_NO_BRAND);
    setViewKey(DEFAULT_VIEW_KEY);
    setSelectedMonthKey(ALL_MONTHS);
    setTablePage(0);
    setSearch("");
  };

  const selectView = (key) => {
    setViewKey(key);
    setSelectedMonthKey(ALL_MONTHS);
  };

  const selectMonth = (monthKey, typeKey) => {
    setViewKey(typeKey);
    setSelectedMonthKey(monthKey);
  };

  const exportMonthwiseGridExcel = () => {
    if (!bundle) {
      toast.error("Nothing to export.");
      return;
    }
    try {
      exportBranchVoucherMonthwiseExcel(bundle);
      toast.success("Excel downloaded — Cover, Monthwise Grid, By Month, Voucher Details");
    } catch (e) {
      toast.error(e?.message || "Excel export failed.");
    }
  };

  const printMonthwiseSummary = () => {
    if (!bundle) {
      toast.error("Nothing to print.");
      return;
    }
    window.print();
  };

  const exportVoucherExcel = () => {
    if (!activeReport?.vouchers?.length) {
      toast.error("Nothing to export for this selection.");
      return;
    }
    try {
      exportBranchVoucherVouchersExcel({
        bundle,
        activeReport,
        activeLabel: activeRow?.label,
        monthKey: selectedMonthKey,
        monthLabel: selectedMonthLabel,
      });
      toast.success("Voucher list Excel downloaded");
    } catch (e) {
      toast.error(e?.message || "Excel export failed.");
    }
  };

  if (!canAccessSalesDataImport(user)) {
    return <div className="p-6 text-center">Access denied — Admin or Storekeeper only.</div>;
  }

  return (
    <div className={`sdic-root bvr-root${dark ? " sdic-dark" : ""}`}>
      <MenuBar />
      <header className="sdic-header bvr-no-print">
        <div>
          <h1>Branch Voucher Reports</h1>
          <p>Month-wise — Outward Supply Br &amp; Credit Sales Br · Inward Supply · Transfer In · Transfer Out</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {filteredVouchers.length > 0 && (
            <button type="button" className="sdic-btn sdic-btn-secondary" onClick={exportVoucherExcel}>
              Excel (vouchers)
            </button>
          )}
          <button type="button" className="sdic-btn sdic-btn-secondary" onClick={() => setDark((d) => !d)}>
            {dark ? "Light" : "Dark"}
          </button>
        </div>
      </header>

      <div className="sdic-body">
        <div className="bvr-no-print">
          <SalesImportNav active="branch-voucher-reports" showBranchFilter={false} />
        </div>

        <section className="sdic-panel bvr-no-print">
          <h2 className="sdic-panel-title">Filters</h2>
          <div className="sdic-week-banner" style={{ flexWrap: "wrap" }}>
            <label>
              Year <span style={{ color: "var(--sdic-accent)" }}>*</span>
              <select
                value={draft.year}
                onChange={(e) => setDraft((d) => ({ ...d, year: Number(e.target.value) }))}
                style={{ padding: "0.4rem", minWidth: "100px" }}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Branch <span style={{ color: "var(--sdic-accent)" }}>*</span>
              <select
                value={draft.branchId}
                onChange={(e) => setDraft((d) => ({ ...d, branchId: e.target.value }))}
                style={{ padding: "0.4rem", minWidth: "160px" }}
              >
                {SALES_IMPORT_BRANCH_OPTIONS.map((id) => (
                  <option key={id} value={id}>
                    {salesImportBranchDisplayName(id)}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Brand 1
              <select
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                disabled={!dataBundle}
                style={{ padding: "0.4rem", minWidth: "180px", maxWidth: "240px" }}
              >
                <option value={BRANCH_VOUCHER_ALL_BRANDS}>All brands</option>
                {brandOptions.map((brand) => (
                  <option key={`b1-${brand}`} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Brand 2
              <select
                value={brandName2}
                onChange={(e) => setBrandName2(e.target.value)}
                disabled={!dataBundle}
                style={{ padding: "0.4rem", minWidth: "180px", maxWidth: "240px" }}
              >
                <option value={BRANCH_VOUCHER_NO_BRAND}>— None —</option>
                {brandOptions
                  .filter((brand) => brand !== brandName || brandName === BRANCH_VOUCHER_ALL_BRANDS)
                  .map((brand) => (
                    <option key={`b2-${brand}`} value={brand}>
                      {brand}
                    </option>
                  ))}
              </select>
            </label>

            <button type="button" className="sdic-btn sdic-btn-primary" onClick={handleSearch} disabled={loading}>
              {loading ? "Searching…" : "Search"}
            </button>
            <button type="button" className="sdic-btn sdic-btn-secondary" onClick={handleReset} disabled={loading}>
              Reset
            </button>
          </div>
          <p style={{ fontSize: "0.83rem", color: "var(--sdic-muted)", margin: "0.5rem 0 0" }}>
            <strong>Brand 1</strong> and <strong>Brand 2</strong> combine into one total (no separate rows per brand).
          </p>
        </section>

        {bundle && (
          <>
            <section className="sdic-panel" id="bvr-monthwise-summary">
              <div className="bvr-monthwise-header">
                <div>
                  <h2 className="sdic-panel-title" style={{ margin: 0 }}>
                    Month-wise summary — {bundle.meta.year}
                  </h2>
                  <p style={{ fontSize: "0.83rem", color: "var(--sdic-muted)", margin: "0.35rem 0 0" }}>
                    {bundle.meta.branchLabel}
                    {bundle.meta.selectedBrands?.length ? ` · ${bundle.meta.brandLabel}` : ""}
                    {" · "}
                    Click a month amount to view vouchers
                  </p>
                </div>
                <div className="bvr-monthwise-actions bvr-no-print">
                  <button type="button" className="sdic-btn sdic-btn-secondary" onClick={exportMonthwiseGridExcel}>
                    Excel
                  </button>
                  <button type="button" className="sdic-btn sdic-btn-secondary" onClick={printMonthwiseSummary}>
                    Print
                  </button>
                </div>
              </div>

              <div className="bvr-print-only bvr-print-header">
                <h1>Branch Voucher Reports — Month-wise Summary {bundle.meta.year}</h1>
                <p>
                  {bundle.meta.branchLabel}
                  {bundle.meta.selectedBrands?.length ? ` · ${bundle.meta.brandLabel}` : ""}
                </p>
              </div>

              <div className="sdic-table-wrap" style={{ maxHeight: "70vh" }}>
                <table className="sdic-table">
                  <thead>
                    <tr>
                      <th rowSpan={2} style={{ position: "sticky", left: 0, zIndex: 2, background: "inherit" }}>
                        Report
                      </th>
                      {monthSlots.map((m) => (
                        <th key={m.monthKey} style={{ textAlign: "center", fontSize: "0.78rem" }}>
                          {m.shortLabel}
                        </th>
                      ))}
                      <th rowSpan={2} style={{ textAlign: "center" }}>
                        Year total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthwiseRows.map((row) => {
                      const isActiveType = viewKey === row.viewKey;
                      return (
                        <tr
                          key={row.viewKey}
                          style={{
                            background: isActiveType ? "var(--sdic-bg-soft, rgba(93,0,129,0.06))" : undefined,
                          }}
                        >
                          <td
                            style={{
                              position: "sticky",
                              left: 0,
                              fontWeight: row.indent ? 500 : 600,
                              background: "inherit",
                              cursor: "pointer",
                              paddingLeft: row.indent ? "1.5rem" : undefined,
                            }}
                            onClick={() => selectView(row.viewKey)}
                            title="Click to select report type"
                          >
                            {row.label}
                            {isActiveType && selectedMonthKey === ALL_MONTHS && (
                              <div style={{ fontSize: "0.72rem", color: "var(--sdic-accent)" }}>Viewing</div>
                            )}
                          </td>
                          {(row.report.months || []).map((month) => {
                            const isActiveCell = isActiveType && selectedMonthKey === month.monthKey;
                            return (
                              <td
                                key={`${row.viewKey}-${month.monthKey}`}
                                style={{
                                  textAlign: "right",
                                  cursor: month.voucherCount > 0 || month.amount > 0 ? "pointer" : "default",
                                  background: isActiveCell
                                    ? "var(--sdic-bg-soft, rgba(93,0,129,0.12))"
                                    : undefined,
                                  fontSize: "0.82rem",
                                }}
                                onClick={() => {
                                  if (month.voucherCount > 0 || month.amount > 0) {
                                    selectMonth(month.monthKey, row.viewKey);
                                  }
                                }}
                                title={
                                  month.voucherCount > 0
                                    ? `${month.voucherCount} invoice(s) · ₹ ${fm(month.amount)}`
                                    : "No vouchers"
                                }
                              >
                                {month.voucherCount > 0 ? (
                                  <>
                                    <div>₹ {fm(month.amount)}</div>
                                    <div style={{ fontSize: "0.72rem", color: "var(--sdic-muted)" }}>
                                      {month.voucherCount} inv
                                    </div>
                                  </>
                                ) : (
                                  <span style={{ color: "var(--sdic-muted)" }}>—</span>
                                )}
                              </td>
                            );
                          })}
                          <td style={{ textAlign: "right", fontWeight: row.indent ? 500 : 700 }}>
                            ₹ {fm(row.report.summary.totalAmount)}
                            <div style={{ fontSize: "0.72rem", fontWeight: 400, color: "var(--sdic-muted)" }}>
                              {row.report.summary.voucherCount} inv
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="sdic-panel bvr-no-print">
              <div className="sdic-week-banner" style={{ flexWrap: "wrap", marginBottom: "0.75rem" }}>
                <h2 className="sdic-panel-title" style={{ margin: 0 }}>
                  {activeRow?.label || "Vouchers"} — {selectedMonthLabel}
                </h2>
                <select
                  value={selectedMonthKey}
                  onChange={(e) => setSelectedMonthKey(e.target.value)}
                  style={{ padding: "0.4rem" }}
                >
                  <option value={ALL_MONTHS}>All months</option>
                  {monthSlots.map((m) => (
                    <option key={m.monthKey} value={m.monthKey}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <input
                  className="sdic-search"
                  style={{ margin: 0, maxWidth: 240 }}
                  placeholder="Search voucher, customer…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setTablePage(0);
                  }}
                />
              </div>
              <div className="sdic-table-wrap" style={{ maxHeight: "65vh" }}>
                <table className="sdic-table">
                  <thead>
                    <tr>
                      <th>Voucher No</th>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Customer</th>
                      <th>Employee</th>
                      <th>Lines</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedVouchers.map((v) => (
                      <tr key={`${v.voucherType}::${v.voucherNo}`}>
                        <td>{v.voucherNo}</td>
                        <td>{v.invoiceDate || "—"}</td>
                        <td>{v.voucherType}</td>
                        <td>{v.customerName || "—"}</td>
                        <td>{v.employeeName || "—"}</td>
                        <td>{v.lineCount}</td>
                        <td>₹ {fm(v.amount)}</td>
                      </tr>
                    ))}
                    {!pagedVouchers.length && (
                      <tr>
                        <td colSpan={7} style={{ textAlign: "center", color: "var(--sdic-muted)" }}>
                          No vouchers for this selection.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {!!filteredVouchers.length && (
                    <tfoot>
                      <tr style={{ fontWeight: 700 }}>
                        <td colSpan={5}>
                          Total ({filteredVouchers.length} voucher{filteredVouchers.length === 1 ? "" : "s"})
                        </td>
                        <td>{filteredVouchers.reduce((s, v) => s + v.lineCount, 0)}</td>
                        <td>₹ {fm(filteredVouchers.reduce((s, v) => s + v.amount, 0))}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              {filteredVouchers.length > RECORDS_PAGE_SIZE && (
                <div className="sdic-week-banner" style={{ marginTop: "0.75rem" }}>
                  <button
                    type="button"
                    className="sdic-btn sdic-btn-secondary"
                    disabled={tablePage === 0}
                    onClick={() => setTablePage((p) => p - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page {tablePage + 1} of {Math.ceil(filteredVouchers.length / RECORDS_PAGE_SIZE)}
                  </span>
                  <button
                    type="button"
                    className="sdic-btn sdic-btn-secondary"
                    disabled={(tablePage + 1) * RECORDS_PAGE_SIZE >= filteredVouchers.length}
                    onClick={() => setTablePage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </section>
          </>
        )}

        {!dataBundle && !loading && (
          <section className="sdic-panel">
            <p style={{ color: "var(--sdic-muted)" }}>
              Select year and branch, then click <strong>Search</strong> to load month-wise totals for all four report types.
            </p>
          </section>
        )}
      </div>
    </div>
  );
}
